import type { PublicSiteConfig } from '@/features/site-config/api.js';

function parseHex(hex: string): { r: number; g: number; b: number } {
  let h = hex.trim().replace(/^#/, '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const n = parseInt(h, 16);
  return { r: (n >> 16) & 0xff, g: (n >> 8) & 0xff, b: n & 0xff };
}

function clamp(v: number): number {
  return Math.max(0, Math.min(255, Math.round(v)));
}

function toHex({ r, g, b }: { r: number; g: number; b: number }): string {
  return '#' + [r, g, b].map((v) => clamp(v).toString(16).padStart(2, '0')).join('');
}

/** Derive hover (lighter 12%), press (darker 12%), and an rgba glow (alpha .4). */
export function hexRamp(hex: string): { hover: string; press: string; glow: string } {
  const { r, g, b } = parseHex(hex);
  const lighten = (v: number) => v + (255 - v) * 0.12;
  const darken = (v: number) => v * 0.88;
  return {
    hover: toHex({ r: lighten(r), g: lighten(g), b: lighten(b) }),
    press: toHex({ r: darken(r), g: darken(g), b: darken(b) }),
    glow: `rgba(${r}, ${g}, ${b}, 0.4)`,
  };
}

/**
 * Apply white-label config to the DOM: CSS-variable ramps for primary/accent,
 * title, favicon, document lang. Called synchronously at boot (snapshot) and
 * again after the fresh fetch resolves.
 */
export function applyBranding(config: PublicSiteConfig | null): void {
  if (!config) return;
  const root = document.documentElement;

  if (config.primaryColor) {
    const ramp = hexRamp(config.primaryColor);
    root.style.setProperty('--primary', config.primaryColor);
    root.style.setProperty('--primary-hover', ramp.hover);
    root.style.setProperty('--primary-press', ramp.press);
    root.style.setProperty('--primary-glow', ramp.glow);
  }

  if (config.secondaryColor) {
    const ramp = hexRamp(config.secondaryColor);
    root.style.setProperty('--accent', config.secondaryColor);
    root.style.setProperty('--accent-strong', ramp.hover);
    root.style.setProperty('--accent-glow', ramp.glow);
  }

  if (config.sitename) document.title = config.sitename;

  if (config.faviconUrl) {
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = config.faviconUrl;
  }

  if (config.defaultLocale) root.lang = config.defaultLocale;
}
